import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { persistStore } from 'src/persist-store';
import { preloadOrgMembers } from 'src/hooks/organization/useOrgMembers';
import { preloadOrgInvites } from 'src/hooks/organization/useOrgInvites';
import { preloadContracts } from 'src/hooks/contracts/useContractOperations';
import { preloadDeals } from 'src/hooks/deals/useDealOperations';
import { preloadFiles } from 'src/hooks/files/useFileOperations';

interface OrgContextType {
  selectedOrgId: string | undefined;
  setSelectedOrgId: (orgId: string | undefined) => void;
  clearSelectedOrg: () => void;
  isInitialized: boolean;
  hasSelectedWorkspace: boolean;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

const ORG_STORAGE_KEY = 'selected-org-id';
const WORKSPACE_SELECTED_KEY = 'workspace-selected';

interface OrgProviderProps {
  children: ReactNode;
}

export const OrgProvider: React.FC<OrgProviderProps> = ({ children }) => {
  const [selectedOrgId, setSelectedOrgIdState] = useState<string | undefined>(undefined);
  const [isInitialized, setIsInitialized] = useState(false);
  const [hasSelectedWorkspace, setHasSelectedWorkspace] = useState(false);

  // Load persisted org on mount
  useEffect(() => {
    const loadPersistedOrg = async () => {
      try {
        const orgId = await persistStore.getSession<string>(ORG_STORAGE_KEY);
        const workspaceSelected = await persistStore.getSession<boolean>(WORKSPACE_SELECTED_KEY);
        if (orgId) {
          setSelectedOrgIdState(orgId);
        }
        if (workspaceSelected) {
          setHasSelectedWorkspace(true);
        }
      } catch (error) {
        console.error('OrgContext: Error loading persisted org:', error);
      } finally {
        setIsInitialized(true);
      }
    };

    loadPersistedOrg();
  }, []);

  const setSelectedOrgId = async (orgId: string | undefined) => {
    setSelectedOrgIdState(orgId);
    setHasSelectedWorkspace(true);

    // Pre-fetch org-specific data for instant navigation
    if (orgId) {
      preloadOrgMembers(orgId);
      preloadOrgInvites(orgId);
      preloadContracts(orgId);
      preloadDeals(orgId);
      preloadFiles(orgId);
    }

    try {
      if (orgId) {
        await persistStore.putSession<string>(ORG_STORAGE_KEY, orgId);
      } else {
        await persistStore.removeSession(ORG_STORAGE_KEY);
      }
      await persistStore.putSession<boolean>(WORKSPACE_SELECTED_KEY, true);
    } catch (error) {
      console.error('OrgContext: Error saving org selection:', error);
    }
  };

  const clearSelectedOrg = async () => {
    setSelectedOrgIdState(undefined);

    try {
      await persistStore.removeSession(ORG_STORAGE_KEY);
    } catch (error) {
      console.error('OrgContext: Error clearing org selection:', error);
    }
  };

  const value: OrgContextType = {
    selectedOrgId,
    setSelectedOrgId,
    clearSelectedOrg,
    isInitialized,
    hasSelectedWorkspace,
  };

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = (): OrgContextType => {
  const context = useContext(OrgContext);
  if (!context) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};